<div class="p-1 py-3">
    <section class="card border-1 rounded-5 p-3 mb-3 bg-light shadow-sm">
        <div class="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
            <div>
                <h6 class="fw-bold text-primary mb-1">Portal forms</h6>
                <p class="text-muted text-xxs mb-0">Each form below shows its sections, questions, and assignment details.</p>
            </div>
        </div>

        <div v-if="loading_portal_forms" class="d-flex justify-content-center align-items-center py-5">
            <Loader />
        </div>

        <div v-else-if="portal_forms.length > 0">
            <div
                v-for="portalForm in portal_forms"
                :key="portalForm.id"
                class="card board-task-card mb-3 border-0 rounded-4 shadow-sm bg-white">
                <div class="card-body p-3 p-md-4">
                    <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                        <div class="flex-grow-1">
                            <div class="d-flex align-items-center gap-2 flex-wrap mb-2">
                                <h6 class="card-title mb-0 fw-bold text-dark">{{ portalForm.form?.name || 'Untitled form' }}</h6>
                            </div>
                            <p v-if="portalForm.form?.description" class="text-xxs text-muted mb-0">
                                {{ truncate_text(portalForm.form.description, 60) }}
                            </p>
                            <div class="d-flex align-items-center gap-2 flex-wrap mt-2 text-xxs text-muted">
                                <div>Created {{ format_date(portalForm.form?.date_created) || '-' }}</div>
                                <span
                                    v-if="is_form_submitted(portalForm.form?.id || portalForm.id)"
                                    class="badge rounded-pill bg-success-subtle text-success ms-auto">
                                    Submitted
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="d-flex align-items-center w-100 gap-3 text-xxs text-muted fw-semibold mt-3 flex-wrap">
                        <span class="d-inline-flex align-items-center gap-1">
                            <i class="bi bi-layout-text-window text-secondary"></i>
                            {{ get_form_sections(portalForm).length }} sections
                        </span>
                        <span class="d-inline-flex align-items-center gap-1">
                            <i class="bi bi-patch-question text-secondary"></i>
                            {{ get_form_question_count(portalForm) }} questions
                        </span>
                        <button
                            class="btn btn-light border rounded-circle text-secondary p-0 px-1 shadow-sm d-inline-flex align-items-center justify-content-center flex-shrink-0 ms-auto"
                            type="button"
                            @click="toggle_form(portalForm.form?.id || portalForm.id)">
                            <i
                                class="bi"
                                :class="is_form_open(portalForm.form?.id || portalForm.id) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                        </button>
                    </div>

                    <div v-if="is_form_open(portalForm.form?.id || portalForm.id)" class="border-top pt-4 mt-4">
                        <div class="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
                            <div>
                                <h6 class="mb-1 fw-bold text-dark">Answer this form</h6>
                                <p class="text-muted text-xxs mb-0">
                                    {{ is_form_submitted(portalForm.form?.id || portalForm.id)
                                      ? 'Your answer has already been submitted.'
                                      : 'Complete the questions below and submit your response.' }}
                                </p>
                            </div>
                        </div>

                        <div
                            v-for="(sectionEntry, sectionIndex) in get_form_sections(portalForm)"
                            :key="`answer-section-${sectionEntry.id || sectionIndex}`"
                            class="mb-4">
                            <div class="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
                                <h6 class="mb-0 fw-bold text-primary text-xxs">
                                    {{ sectionIndex + 1 }}. {{ sectionEntry.section?.name || 'Untitled section' }}
                                </h6>
                            </div>

                            <div
                                v-for="(questionSection, questionIndex) in sectionEntry.section?.question_sections || []"
                                :key="`answer-question-${questionSection.id || questionIndex}`"
                                class="p-3 rounded-4 border bg-light mb-3">
                                <div class="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-2">
                                    <label class="form-label fw-semibold text-dark text-xxs mb-0">
                                        {{ questionIndex + 1 }}. {{ questionSection.question?.question || 'Untitled question' }}
                                        <span v-if="is_question_required(questionSection)" class="text-danger">*</span>
                                    </label>
                                    <span class="badge rounded-pill bg-white text-secondary border text-xxs">
                                        {{ get_question_type(questionSection) }}
                                    </span>
                                </div>

                                <input
                                    v-if="get_question_type(questionSection) === 'text'"
                                    type="text"
                                    class="form-control text-xxs rounded-3"
                                    :value="get_question_value(questionSection.question?.id)"
                                    :disabled="is_form_locked(portalForm)"
                                    @input="set_question_value(questionSection.question?.id, $event.target.value)"
                                    :placeholder="questionSection.question?.question || 'Enter your answer'" />

                                <textarea
                                    v-else-if="get_question_type(questionSection) === 'textarea'"
                                    class="form-control text-xxs rounded-3"
                                    rows="3"
                                    :value="get_question_value(questionSection.question?.id)"
                                    :disabled="is_form_locked(portalForm)"
                                    @input="set_question_value(questionSection.question?.id, $event.target.value)"
                                    :placeholder="questionSection.question?.question || 'Enter your answer'"></textarea>

                                <input
                                    v-else-if="get_question_type(questionSection) === 'number'"
                                    type="number"
                                    class="form-control text-xxs rounded-3"
                                    :value="get_question_value(questionSection.question?.id)"
                                    :disabled="is_form_locked(portalForm)"
                                    @input="set_question_value(questionSection.question?.id, $event.target.value)"
                                    :placeholder="questionSection.question?.question || 'Enter a number'" />

                                <input
                                    v-else-if="get_question_type(questionSection) === 'date'"
                                    type="date"
                                    class="form-control text-xxs rounded-3"
                                    :value="get_question_value(questionSection.question?.id)"
                                    :disabled="is_form_locked(portalForm)"
                                    @input="set_question_value(questionSection.question?.id, $event.target.value)" />

                                <div v-else-if="get_question_type(questionSection) === 'checkbox'">
                                    <label
                                        v-for="option in get_question_options(questionSection)"
                                        :key="option.id || option.option"
                                        class="d-flex align-items-center gap-2 p-2 rounded-3 bg-white border mb-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            class="form-check-input"
                                            :name="`portal-form-question-${questionSection.question?.id}`"
                                            :value="option.option"
                                            :checked="get_question_value(questionSection.question?.id) === option.option"
                                            :disabled="is_form_locked(portalForm)"
                                            @change="set_question_value(questionSection.question?.id, option.option)" />
                                        <span class="text-xxs text-dark">{{ option.option }}</span>
                                    </label>
                                </div>

                                <div v-else-if="get_question_type(questionSection) === 'selection'">
                                    <label
                                        v-for="option in get_question_options(questionSection)"
                                        :key="option.id || option.option"
                                        class="d-flex align-items-center gap-2 p-2 rounded-3 bg-white border mb-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            class="form-check-input"
                                            :value="option.option"
                                            :checked="Array.isArray(get_question_value(questionSection.question?.id)) && get_question_value(questionSection.question?.id).includes(option.option)"
                                            :disabled="is_form_locked(portalForm)"
                                            @change="toggle_checkbox_answer(questionSection.question?.id, option.option, $event.target.checked)" />
                                        <span class="text-xxs text-dark">{{ option.option }}</span>
                                    </label>
                                </div>

                                <div v-else-if="get_question_type(questionSection) === 'file'">
                                    <input
                                        type="file"
                                        class="form-control text-xxs rounded-3"
                                        :disabled="is_form_locked(portalForm)"
                                        @change="handle_file_upload($event, questionSection.question?.id)" />
                                    <div v-if="get_selected_file_name(questionSection.question?.id)" class="text-muted text-xxs mt-2">
                                        Selected: {{ get_selected_file_name(questionSection.question?.id) }}
                                    </div>
                                </div>

                                <input
                                    v-else
                                    type="text"
                                    class="form-control text-xxs rounded-3"
                                    :value="get_question_value(questionSection.question?.id)"
                                    :disabled="is_form_locked(portalForm)"
                                    @input="set_question_value(questionSection.question?.id, $event.target.value)"
                                    :placeholder="questionSection.question?.question || 'Enter your answer'" />

                                <p v-if="get_question_error(questionSection.question?.id)" class="text-danger text-xxs mt-2 mb-0">
                                    {{ get_question_error(questionSection.question?.id) }}
                                </p>
                            </div>
                        </div>

                        <div v-if="!is_form_locked(portalForm)" class="d-flex justify-content-end align-items-center gap-2 mt-3 pt-2 border-top">
                            <button
                                type="button"
                                class="btn btn-primary btn-sm rounded-3 px-3"
                                :disabled="is_form_submitting(portalForm.form?.id || portalForm.id)"
                                @click="submit_portal_form(portalForm)">
                                {{ is_form_submitting(portalForm.form?.id || portalForm.id) ? 'Submitting...' : 'Submit answers' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="d-flex flex-column align-items-center justify-content-center text-center text-muted py-5 gap-3">
            <i class="bi bi-clipboard-check fs-1 text-primary"></i>
            <p class="mb-0 text-xs">No forms have been assigned to this portal yet.</p>
        </div>
    </section>
</div>
